import { createContext, useContext, useEffect, useMemo, useRef, type ReactNode } from 'react';

/**
 * Канал «страница → оболочка» (FE-192, FE-193).
 *
 * Страница не рисует навигацию и не знает о семи шагах: она сообщает оболочке
 * стадию своего заказа и содержимое двух рельсов, а трекер, шапку и раскладку
 * рисует `AppLayout`. Обратной зависимости нет — `pages/` не импортирует `app/`.
 *
 * Контракт живёт в `shared`, потому что им пользуются оба конца: страницы его
 * заполняют, оболочка — читает. Тип стадии здесь строковый, а не `OrderStage`
 * из `entities`: слой `shared` не имеет права зависеть от вышележащего слоя.
 * Сужает тип `AppLayout`, который стадию и потребляет.
 */

/**
 * Список этапов («Сейчас я делаю для вас») здесь **намеренно отсутствует.**
 *
 * Он повторял то, что уже говорит трекер семи шагов в шапке, только другими
 * словами и на четверть экрана — а на четвёрке ещё и отбирал место у диалога,
 * ради которого рельс и существует. Рельс несёт личность Karl и переписку;
 * состояние заказа показывает трекер.
 */
export interface KarlRailContent {
  /**
   * Последняя реплика Karl. Текст уже переведён вызывающим экраном: в чате
   * `content` ассистента — ключ i18n, а не готовая строка (CLAUDE.md §3).
   */
  message?: string | undefined;
  /** Время реплики в локали пользователя. */
  messageTime?: string | undefined;
  /** Прогресс текущего этапа, 0–100. Только настоящее число с сервера. */
  progressPercent?: number | undefined;
  progressLabel?: string | undefined;
  /**
   * Дополнительный блок рельса: диалог с Karl, критерии и т. п.
   *
   * Отправки сообщения в контракте нет намеренно. Переписку несёт сам диалог
   * (`KarlDialogue`), и он приходит сюда: у рельса нет ни своего поля ввода, ни
   * своего представления о том, куда уходит сообщение.
   */
  extra?: ReactNode | undefined;
}

export interface AppShellSlots {
  /** Стадия заказа. `null` — экран вне воронки: трекер не рисуется. */
  stage: string | null;
  paid: boolean;
  /** Содержимое рельса Karl. `null` — рельс не рисуется. */
  karl: KarlRailContent | null;
  /** Содержимое правого сайдбара. */
  context: ReactNode;
  /** Доступное имя правого сайдбара. */
  contextLabel: string;
  /**
   * Переписка выбранного автомобиля — цель шага 5 трекера.
   *
   * Передаёт её только экран, у которого идентификатор **уже есть** в
   * загруженных данных (четвёрка после выбора, заключение, сопровождение):
   * отдельного запроса ради одной ссылки в шапке нет. Не передан — шаг 5
   * ведёт в список переписок.
   */
  negotiationId?: string | null | undefined;
  /**
   * Заказ экрана, когда его нет в адресе.
   *
   * Шаги трекера ведут на экраны заказа, и оболочка берёт его из адреса
   * `/app/searches/:searchId/...`. Карточка переписки живёт вне этого адреса
   * (`/app/negotiations/:id`) и знает заказ из своих данных — она и передаёт
   * его сюда. Не передан и нет в адресе — трекер остаётся индикатором без
   * переходов: кнопка, которой некуда вести, хуже отсутствия кнопки.
   */
  searchId?: string | null | undefined;
}

export const EMPTY_SLOTS: AppShellSlots = {
  stage: null,
  paid: false,
  karl: null,
  context: null,
  contextLabel: '',
  negotiationId: null,
  searchId: null,
};

/**
 * Переход к следующему шагу — главное действие экрана, которое на телефоне
 * закреплено снизу в одной полосе с пузырём Karl (FE-585, DESIGN_SYSTEM §4.4).
 *
 * **Только переход, никогда не исполнение.** Полоса несёт ссылку на следующий
 * экран («Karl beauftragen», «Weiter zur Übergabe») или открытие окна входа —
 * то, что ничего не отправляет и не списывает. Отправка форм и необратимые
 * действия (оплата, мандат, поручение, согласие с предложением) остаются в
 * потоке экрана: рядом с ними согласия и финальная сверка, которые обязаны
 * быть прочитаны до нажатия (MVP_PRD §38.3), а закреплённая кнопка позволила
 * бы нажать мимо них.
 *
 * Объявляет переход не страница через `useAppShell`, а сам компонент кнопки
 * (`NextStepAction`) при монтировании: главное действие часто живёт глубоко в
 * фиче и появляется по условию, и второе описание того же условия на уровне
 * страницы рано или поздно разошлось бы с первым.
 */
export type ShellNextStep =
  | { label: string; to: string; onClick?: undefined }
  | { label: string; onClick: () => void; to?: undefined };

/**
 * В контексте лежит **только сеттер, и он обязан быть стабильным**.
 *
 * Класть сюда сами слоты нельзя: значение контекста менялось бы при каждом
 * обновлении, эффект в `useAppShell` перезапускался бы от этого изменения и
 * снова вызывал сеттер — рендер уходил в бесконечный цикл. Внешне это выглядит
 * как «адрес сменился, а экран прежний»: React не успевает закоммитить переход.
 * Слоты читает сам `AppLayout` из своего состояния — больше они никому не нужны.
 */
export interface AppShellStore {
  setSlots: (slots: AppShellSlots) => void;
  setNextStep: (step: ShellNextStep | null) => void;
  /**
   * Перевести человека к полю ввода Karl (Epic 58, `FE-586`, `FE-587`): на
   * широком экране — фокус в поле рельса, уже 1024 px — открыть лист Karl и
   * поставить фокус туда. Экран не знает, где сейчас живёт диалог, — это знает
   * оболочка. Тоже стабильная ссылка, по той же причине, что и сеттеры.
   *
   * `prefill` — начало сообщения («Ich möchte meine Suche anpassen: »): оно
   * встаёт в **пустое** поле, курсор — в конец. Набранный человеком черновик
   * не перетирается никогда — тогда только фокус.
   */
  focusKarl?: ((prefill?: string) => void) | undefined;
}

/** Провайдер ставит `AppLayout`; страницы пользуются только `useAppShell`. */
export const AppShellContext = createContext<AppShellStore | null>(null);

/**
 * Заполнить оболочку с экрана.
 *
 * Значения сбрасываются при размонтировании: иначе критерии одного заказа
 * оставались бы в сайдбаре следующего.
 */
export function useAppShell(slots: Partial<AppShellSlots>): void {
  const store = useContext(AppShellContext);

  // Объект пересоздаётся на каждый рендер страницы, поэтому в зависимости
  // уходят сами значения, а не ссылка: иначе эффект зациклился бы.
  const {
    stage = null,
    paid = false,
    karl = null,
    context = null,
    contextLabel = '',
    negotiationId = null,
    searchId = null,
  } = slots;
  const value = useMemo<AppShellSlots>(
    () => ({ stage, paid, karl, context, contextLabel, negotiationId, searchId }),
    [stage, paid, karl, context, contextLabel, negotiationId, searchId],
  );

  useEffect(() => {
    if (!store) return;
    store.setSlots(value);
    return () => {
      store.setSlots(EMPTY_SLOTS);
    };
  }, [store, value]);
}

/**
 * Объявить оболочке переход к следующему шагу (FE-585).
 *
 * Возвращает, есть ли оболочка вокруг: вне неё (тесты экрана, публичные
 * страницы) полосы нет, и кнопка остаётся в потоке на любой ширине.
 *
 * Обработчик нажатия хранится по ссылке: он пересоздаётся на каждый рендер, и
 * в зависимостях эффекта перерегистрировал бы переход при каждом рендере.
 */
export function useShellNextStep(step: ShellNextStep): boolean {
  const store = useContext(AppShellContext);
  const onClickRef = useRef(step.onClick);
  useEffect(() => {
    onClickRef.current = step.onClick;
  });

  const { label, to } = step;
  const clickable = step.onClick !== undefined;

  useEffect(() => {
    if (!store) return;
    store.setNextStep(
      to === undefined
        ? {
            label,
            onClick: () => {
              onClickRef.current?.();
            },
          }
        : { label, to },
    );
    return () => {
      store.setNextStep(null);
    };
  }, [store, label, to, clickable]);

  return store !== null;
}

/**
 * Действие «к Karl» для кнопок экрана: «Mit Karl sprechen», «Suche anpassen».
 *
 * Возвращает `null` вне оболочки: кнопка, которой некуда вести, не рисуется
 * (DESIGN_SYSTEM §8). Звать её стоит только с экранов, у которых рельс Karl
 * есть (`karl !== null`). Перехода на экран диалога здесь нет и быть не может —
 * диалог стоит рядом, в рельсе (CLAUDE.md §8).
 *
 * Необязательный аргумент — начало сообщения для пустого поля (см.
 * `AppShellStore.focusKarl`). Обработчику кнопки функцию напрямую не отдают:
 * `onClick={focusKarl}` передал бы событие нажатия вместо текста.
 */
export function useFocusKarl(): ((prefill?: string) => void) | null {
  const store = useContext(AppShellContext);
  return store?.focusKarl ?? null;
}
